/**
 * Component: Upload bolletta (PDF o manuale)
 * 
 * PDF flow:
 * 1. Upload PDF → backend auto-OCR + save atomico (bollette + dettagli)
 * 2. Success → bolletta salvata con dettagli completi
 * 3. Failure → fallback a inserimento manuale
 * 
 * Manual flow:
 * 1. User fills form → POST /bollette (JSON)
 * 2. Backend saves bolletta senza dettagli OCR
 */

import React, { useState } from 'react';
import api from '../utils/api';
import './BolletteUpload.css';

function BolletteUpload({ onUploadSuccess }) {
  const [mode, setMode] = useState('manual'); // 'manual' | 'pdf'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    data: '',
    tipo: 'luce',
    provider: '',
    kwh: '',
    m3: '',
    importo: '',
    periodo_mesi: '1',
    periodo_inizio: '',
    periodo_fine: '',
    nota: ''
  });
  
  const [pdfFile, setPdfFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // File size validation (issue #134 Phase 5)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        setMessage({ type: 'error', text: 'File troppo grande (max 20MB)' });
        return;
      }
      
      if (file.type === 'application/pdf') {
        setPdfFile(file);
        setMessage(null);
      } else {
        setMessage({ type: 'error', text: 'Solo file PDF sono supportati' });
      }
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // File size validation (issue #134 Phase 5)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        setMessage({ type: 'error', text: 'File troppo grande (max 20MB)' });
        return;
      }
      
      if (file.type === 'application/pdf') {
        setPdfFile(file);
        setMessage(null);
      } else {
        setMessage({ type: 'error', text: 'Solo file PDF sono supportati' });
      }
    }
  };

  const validateManualForm = () => {
    if (!formData.data) return 'Data è obbligatoria';
    if (!formData.importo || parseFloat(formData.importo) <= 0) return 'Importo deve essere > 0';
    
    if (formData.tipo === 'luce' && formData.kwh && parseFloat(formData.kwh) < 0) {
      return 'kWh non valido';
    }
    if (formData.tipo === 'gas' && formData.m3 && parseFloat(formData.m3) < 0) {
      return 'm³ non valido';
    }
    
    return null;
  };

  const resetForm = () => {
    setFormData({
      data: '',
      tipo: 'luce',
      provider: '',
      kwh: '',
      m3: '',
      importo: '',
      periodo_mesi: '1',
      periodo_inizio: '',
      periodo_fine: '',
      nota: ''
    });
  };



  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateManualForm();
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const payload = {
        data: formData.data,
        tipo: formData.tipo,
        provider: formData.provider || 'N/A',
        importo: parseFloat(formData.importo),
        periodo_mesi: parseInt(formData.periodo_mesi) || 1,
      };

      if (formData.periodo_inizio) payload.periodo_inizio = formData.periodo_inizio;
      if (formData.periodo_fine) payload.periodo_fine = formData.periodo_fine;

      if (formData.tipo === 'luce' && formData.kwh) {
        payload.kwh = parseFloat(formData.kwh);
      }
      if (formData.tipo === 'gas' && formData.m3) {
        payload.m3 = parseFloat(formData.m3);
      }
      if (formData.nota) {
        payload.nota = formData.nota;
      }

      const response = await api.post('/bollette', payload);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Bolletta salvata con successo! ✅' });
        resetForm();
        
        if (onUploadSuccess) {
          onUploadSuccess(response.data.data);
        }
      }
    } catch (err) {
      console.error('Errore submit manuale:', err);
      const errorMsg = err.response?.data?.error || 'Errore durante il salvataggio';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handlePdfSubmit = async (e) => {
    e.preventDefault();
    
    if (!pdfFile) {
      setMessage({ type: 'error', text: 'Seleziona un file PDF' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const formDataMultipart = new FormData();
      formDataMultipart.append('file', pdfFile);

      // Upload PDF → backend auto-OCR + salva bolletta + dettagli in transazione
      const uploadResponse = await api.post('/bollette', formDataMultipart, {
        headers: { 'Content-Type': 'multipart/form-data' },
        validateStatus: (status) => status < 500 // Accept 2xx, 3xx, 4xx (422 will be handled)
      });

      if (uploadResponse.data.success) {
        // Bolletta salvata con successo (OCR + DB commit)
        setMessage({ 
          type: 'success', 
          text: '✅ Bolletta caricata e salvata con successo!' 
        });
        resetForm();
        setPdfFile(null);
        
        if (onUploadSuccess) {
          onUploadSuccess(uploadResponse.data.data);
        }
      } else {
        // Fallback a inserimento manuale se backend non riesce a processare
        setMode('manual');
        setMessage({
          type: 'error',
          text: '❌ Impossibile processare il PDF. Inserisci i dati manualmente.'
        });
      }
    } catch (err) {
      console.error('Errore upload PDF:', err);
      // On any error, fall back to manual
      setMode('manual');
      const errorMsg = err.response?.data?.error || 'Errore durante l\'upload del PDF';
      setMessage({ type: 'error', text: `❌ ${errorMsg}. Inserisci i dati manualmente.` });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="bollette-upload">
      <h2>📤 Carica Bolletta</h2>
      
      {/* Mode selector */}
      <div className="mode-selector">
        <button
          className={mode === 'manual' ? 'active' : ''}
          onClick={() => setMode('manual')}
        >
          ✏️ Inserimento Manuale
        </button>
        <button
          className={mode === 'pdf' ? 'active' : ''}
          onClick={() => setMode('pdf')}
        >
          📄 Upload PDF
        </button>
      </div>

      {/* Messaggi feedback */}
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}



      {/* Form Manuale */}
      {mode === 'manual' && (
        <form onSubmit={handleManualSubmit} className="manual-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="data">Data bolletta *</label>
              <input
                type="date"
                id="data"
                name="data"
                value={formData.data}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Tipo *</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="tipo"
                    value="luce"
                    checked={formData.tipo === 'luce'}
                    onChange={handleInputChange}
                  />
                  ⚡ Luce
                </label>
                <label>
                  <input
                    type="radio"
                    name="tipo"
                    value="gas"
                    checked={formData.tipo === 'gas'}
                    onChange={handleInputChange}
                  />
                  🔥 Gas
                </label>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="provider">Provider</label>
              <input
                type="text"
                id="provider"
                name="provider"
                value={formData.provider}
                onChange={handleInputChange}
                placeholder="es. Enel, Eni, Engie..."
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="importo">Importo € *</label>
              <input
                type="number"
                id="importo"
                name="importo"
                value={formData.importo}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="periodo_mesi">Periodo (mesi)</label>
              <input
                type="number"
                id="periodo_mesi"
                name="periodo_mesi"
                value={formData.periodo_mesi}
                onChange={handleInputChange}
                min="1"
                max="12"
                title="1 = mensile, 2 = bimestrale, ecc."
              />
            </div>
          </div>

          <div className="form-row">
            {formData.tipo === 'luce' && (
              <div className="form-group">
                <label htmlFor="kwh">kWh consumati</label>
                <input
                  type="number"
                  id="kwh"
                  name="kwh"
                  value={formData.kwh}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                />
              </div>
            )}
            
            {formData.tipo === 'gas' && (
              <div className="form-group">
                <label htmlFor="m3">m³ consumati</label>
                <input
                  type="number"
                  id="m3"
                  name="m3"
                  value={formData.m3}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="nota">Note (opzionale)</label>
            <textarea
              id="nota"
              name="nota"
              value={formData.nota}
              onChange={handleInputChange}
              rows="3"
              placeholder="Note aggiuntive..."
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? '⏳ Salvataggio...' : '💾 Salva Bolletta'}
          </button>
        </form>
      )}

      {/* Form PDF Upload */}
      {mode === 'pdf' && (
        <form onSubmit={handlePdfSubmit} className="pdf-form">
          <div
            className={`drop-zone ${dragActive ? 'active' : ''} ${pdfFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {pdfFile ? (
              <div className="file-info">
                <p>📄 {pdfFile.name}</p>
                <p className="file-size">{(pdfFile.size / 1024).toFixed(2)} KB</p>
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => setPdfFile(null)}
                >
                  ✖ Rimuovi
                </button>
              </div>
            ) : (
              <>
                <p className="drop-text">📁 Trascina qui il PDF della bolletta</p>
                <p className="drop-or">oppure</p>
                <label htmlFor="file-input" className="btn-secondary">
                  Scegli File
                </label>
                <input
                  type="file"
                  id="file-input"
                  accept="application/pdf"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                <p className="file-size-hint">⚠️ Max 20MB</p>
              </>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !pdfFile}
          >
            {loading ? '⏳ Analisi in corso...' : '📤 Analizza PDF'}
          </button>
        </form>
      )}
    </div>
  );
}

export default BolletteUpload;
